import { useEffect } from 'react';

import cn from 'classnames';
import { Controller, useForm } from 'react-hook-form';

import { EditableSettings } from 'components/EditableSettings/EditableSettings';
import { Button, Checkbox, EditableTitle, Input, InputGroup } from 'components/FormComponents';
import { Select } from 'components/FormComponents';

export const CheckTypeEdit = ({ checkType, onClose = () => {}, onSave = () => {} }) => {
  const {
    control,
    register,
    reset,
    watch,
    handleSubmit,
    formState: { errors },
  } = useForm();

  // Reset form on check change
  useEffect(() => {
    reset(checkType);
  }, [checkType]);

  const selectCheckTypeOptions = [
    { value: 'Credit background checks', label: 'Credit background checks' },
    { value: 'Criminal record check', label: 'Criminal record check' },
    { value: 'Employment checks', label: 'Employment checks' },
    { value: 'Identity check', label: 'Identity check' },
    { value: 'MVR check', label: 'MVR check' },
    {
      value: 'Professional License & Education Background Checks',
      label: 'Professional License & Education Background Checks',
    },
    { value: 'Reference checks', label: 'Reference checks' },
    { value: 'Other checks', label: 'Other checks' },
  ];

  return (
    <form className="card card-with-border u-width-75" onSubmit={handleSubmit(onSave)}>
      <div className="u-padding u-padding-bottom--0">
        <div className={cn('u-flex-box', 'u-flex-align-center')}>
          <EditableTitle register={register} name="title" value={watch('title')} />
        </div>
        <p className="t-small">Configure and manage the check below</p>
      </div>
      <hr />
      <div className="u-margin">
        <InputGroup title="ID">
          <Input disabled={true} type="number" name="id" register={register} />
        </InputGroup>

        <InputGroup
          title="Slug"
          description='code of check. No spaces, use "_" (underscore) instead'>
          <Input name="slug" register={register} />
        </InputGroup>

        <InputGroup title="NZD Pricing" description="Price in NZD">
          <Input
            type="number"
            name="pricing_nzd"
            register={register}
            inputProps={{ step: '0.01' }}
          />
        </InputGroup>

        <InputGroup title="USD Pricing" description="Price in USD">
          <Input
            type="number"
            name="pricing_usd"
            register={register}
            inputProps={{ step: '0.01' }}
          />
        </InputGroup>

        <InputGroup title="AUD Pricing" description="Price in AUD">
          <Input
            type="number"
            name="pricing_aud"
            register={register}
            inputProps={{ step: '0.01' }}
          />
        </InputGroup>

        <InputGroup title="Cost" description="Cost of check in NZD">
          <Input type="number" name="cost" register={register} inputProps={{ step: '0.01' }} />
        </InputGroup>

        <InputGroup
          title="Candidate tab"
          description='Last part of URL when going to candidate profile. Default to "background_checks"'>
          <Input name="candidate_tab" register={register} />
        </InputGroup>

        <InputGroup title="Result type" description='["file", "link"]. Default to "file"'>
          <Input name="result_type" register={register} />
        </InputGroup>

        <InputGroup
          title="Provider launcher class"
          description="Class for launching when check is created">
          <Input name="provider_launcher_class" register={register} />
        </InputGroup>

        <InputGroup title="Result flag" description="Labels for checks with results option">
          <EditableSettings
            title="Enable result flag"
            open={watch('has_result_flag')}
            toggleable
            register={register}
            name="has_result_flag"
            className="u-margin-0">
            <InputGroup title="True Label" description="Label for when check has results">
              <Input name="true_flag_label" register={register} />
            </InputGroup>
            <InputGroup title="False label" description="Label for when check don't have results">
              <Input name="false_flag_label" register={register} />
            </InputGroup>
          </EditableSettings>
        </InputGroup>

        <InputGroup title="Background job" description="Job to launch/complete check in background">
          <EditableSettings
            title="Enable background job"
            open={watch('has_job')}
            toggleable
            register={register}
            name="has_job"
            className="u-margin-0">
            <InputGroup title="Job Class" description="PHP job class">
              <Input name="job_class" register={register} />
            </InputGroup>
            <Checkbox
              name="automatic_job"
              register={register}
              checked={watch('automatic_job')}
              label="Is automatic"
            />
          </EditableSettings>
        </InputGroup>

        <InputGroup title="Provider form class" description="PHP class of provider form">
          <Input name="provider_form_class" register={register} />
        </InputGroup>

        <InputGroup title="Check Group" description="Group for filtering in store-front">
          <Select name="check_group" value={watch('check_group')} register={register}>
            {selectCheckTypeOptions.map(option => (
              <Select.Item key={option.value} value={option.value}>
                {option.label}
              </Select.Item>
            ))}
          </Select>
        </InputGroup>

        <InputGroup
          title="Support user"
          description="Default support user to assign to checks created with this check type">
          <Input name="support_user" register={register} />
        </InputGroup>

        <InputGroup
          title="Provider Follow Up Report Days"
          description="Number of days before check is included in the provider follow up report">
          <Input type="number" name="provider_follow_up_period" register={register} />
        </InputGroup>

        <Checkbox
          name="signature_required"
          register={register}
          checked={watch('signature_required')}
          label="Requires signature"
        />
        <InputGroup title="Minimum age" description="Minimum age for the check">
          <Input type="number" name="min_age" register={register} />
        </InputGroup>
        <Checkbox
          name="has_expiration_date"
          register={register}
          checked={watch('has_expiration_date')}
          label="Has expiration date"
        />
        <Checkbox
          name="set_submitted_by_provider"
          register={register}
          checked={watch('set_submitted_by_provider')}
          label='Set as "Submitted" when submitted by candidate'
        />
        <Checkbox
          name="complete_on_submit"
          register={register}
          checked={watch('complete_on_submit')}
          label='Set as "Completed" when submitted by candidate'
        />
        <Checkbox
          name="has_id_requirements"
          register={register}
          checked={watch('has_id_requirements')}
          label="Requires Photo ID"
        />
        <Checkbox
          name="has_fcra"
          register={register}
          checked={watch('has_fcra')}
          label="Requires FCRA"
        />
        <Checkbox
          name="is_manual"
          register={register}
          checked={watch('is_manual')}
          label="Manually completed by Support team"
        />
      </div>
      <div className={cn('card_footer', 'u-flex-box', 'u-flex-justify-between')}>
        <Button onClick={onClose} type="secondary">
          Cancel
        </Button>
        <Button submit disabled={true}>
          Save
        </Button>
      </div>
    </form>
  );
};
